/* @jsx h */
import { defineComponent, h, ref, onMounted } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import {
  useLocalization,
  RightSheet,
  Loading,
  ErrorRenderer,
  ResponsiveTable,
  UserAvatar,
} from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import type { Staff } from "@lemmonhq/types";
import { setTitle } from "@lemmonhq/orders";
import { useTrpc } from "@app/vue/providers/index";
import { useToast } from "@app/vue/providers/Toast";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { StaffForm } from "./StaffForm";
import type { StaffFormState } from "./StaffForm";

export const StaffSettings = defineComponent({
  name: "StaffSettings",
  setup() {
    const { t } = useLocalization();
    const toast = useToast();
    const trpc = useTrpc();
    const queryClient = useQueryClient();

    const isSheetOpen = ref(false);
    const selectedStaff = ref<Staff | null>(null);
    const mode = ref<"create" | "edit">("create");

    const {
      data: staffList,
      isPending,
      isError,
      error,
      refetch,
    } = useQuery({
      queryKey: ["staff", "list"],
      queryFn: () => trpc.staff.list.query(),
    });

    const createMutation = useMutation({
      mutationFn: (input: {
        name: string;
        email?: string | null;
        password?: string | null;
        pin?: string | null;
        staff_type?: "Front of house" | "Kitchen" | "Bar" | "Delivery";
        role_name?: "staff" | "manager";
        print_receipt?: number;
      }) => trpc.staff.create.mutate(input),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["staff"] });
        toast.showToast(t("settings.staff.toast_created"), { type: "success" });
      },
      onError: (err) => {
        console.error("Failed to create staff member", err);
        toast.showToast(t("settings.staff.toast_create_error"), {
          type: "error",
        });
      },
    });

    const updateMutation = useMutation({
      mutationFn: (input: {
        id: number;
        data: {
          name?: string;
          email?: string | null;
          password?: string;
          pin?: string | null;
          staff_type?: "Front of house" | "Kitchen" | "Bar" | "Delivery";
          role_name?: "staff" | "manager";
          print_receipt?: number;
        };
      }) => trpc.staff.update.mutate(input),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["staff"] });
        toast.showToast(t("settings.staff.toast_updated"), { type: "success" });
      },
      onError: (err) => {
        console.error("Failed to update staff member", err);
        toast.showToast(t("settings.staff.toast_update_error"), {
          type: "error",
        });
      },
    });

    const deleteMutation = useMutation({
      mutationFn: (id: number) => trpc.staff.delete.mutate({ id }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["staff"] });
        toast.showToast(t("settings.staff.toast_deleted"), { type: "success" });
      },
      onError: (err) => {
        console.error("Failed to delete staff member", err);
        toast.showToast(t("settings.staff.toast_delete_error"), {
          type: "error",
        });
      },
    });

    const openCreateSheet = () => {
      mode.value = "create";
      selectedStaff.value = null;
      isSheetOpen.value = true;
    };

    const openEditSheet = (staff: Staff) => {
      mode.value = "edit";
      selectedStaff.value = staff;
      isSheetOpen.value = true;
    };

    const closeSheet = () => {
      isSheetOpen.value = false;
      selectedStaff.value = null;
    };

    const handleCreate = async (formData: StaffFormState) => {
      await createMutation.mutateAsync({
        name: formData.name,
        email: formData.email.trim() || null,
        password: formData.password || null,
        pin: formData.pin.trim() || null,
        staff_type: formData.staff_type as any,
        role_name: formData.role_name as any,
        print_receipt: formData.print_receipt,
      });
      closeSheet();
    };

    const handleUpdate = async (id: number, formData: StaffFormState) => {
      const data: Record<string, any> = {
        name: formData.name,
        email: formData.email.trim() || null,
        // Empty = legacy value with no matching option; leave it unchanged.
        staff_type: formData.staff_type || undefined,
        role_name: formData.role_name,
        print_receipt: formData.print_receipt,
      };
      if (formData.password) data.password = formData.password;
      if (formData.pin.trim()) data.pin = formData.pin.trim();
      await updateMutation.mutateAsync({ id, data });
      closeSheet();
    };

    const handleDelete = async (id: number) => {
      await deleteMutation.mutateAsync(id);
      closeSheet();
    };

    onMounted(() => {
      setTitle(t("settings.staff.title"));
    });

    const columns: ResponsiveTableColumn[] = [
      {
        key: "d-name",
        label: t("settings.staff.col_name"),
        icon: "person",
        mobilePriority: "hidden",
        render: (staff: Staff) => (
          <div class="flex items-center gap-3">
            <UserAvatar name={staff.name} email={staff.email} size="sm" />
            <div>
              <div class="font-medium text-base-content">{staff.name}</div>
              <div class="text-xs text-base-content/60">{staff.email}</div>
            </div>
          </div>
        ),
      },
      {
        key: "d-role",
        label: t("settings.staff.col_role"),
        icon: "badge",
        mobilePriority: "hidden",
        render: (staff: Staff) => (
          <span class="badge badge-sm badge-outline capitalize">
            {staff.role}
          </span>
        ),
      },
      {
        key: "d-type",
        label: t("settings.staff.col_type"),
        icon: "work",
        mobilePriority: "hidden",
        render: (staff: Staff) => (
          <span class="text-sm text-base-content/80">{staff.staff_type}</span>
        ),
      },
      {
        key: "d-status",
        label: t("settings.staff.col_status"),
        icon: "circle",
        mobilePriority: "hidden",
        render: (staff: Staff) => (
          <span
            class={`badge badge-sm ${staff.status === "active" ? "badge-success" : "badge-neutral"}`}
          >
            {staff.status}
          </span>
        ),
      },
      {
        key: "m-name",
        label: t("settings.staff.col_name"),
        desktopHide: true,
        mobilePriority: "primary",
        render: (staff: Staff) => (
          <div class="flex items-center gap-2.5 min-w-0">
            <UserAvatar name={staff.name} email={staff.email} size="sm" />
            <div class="min-w-0">
              <div class="truncate text-sm font-medium text-base-content">
                {staff.name}
              </div>
              <div class="truncate text-xs text-base-content/60">
                {staff.email}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "m-role",
        label: t("settings.staff.col_role"),
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass: "flex items-center shrink-0",
        render: (staff: Staff) => (
          <span class="badge badge-sm badge-outline capitalize">
            {staff.role}
          </span>
        ),
      },
      {
        key: "m-type",
        label: t("settings.staff.col_type"),
        desktopHide: true,
        mobilePriority: "secondary",
        render: (staff: Staff) => staff.staff_type,
      },
      {
        key: "m-status",
        label: t("settings.staff.col_status"),
        desktopHide: true,
        mobilePriority: "secondary",
        render: (staff: Staff) => staff.status,
      },
      {
        key: "actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (staff: Staff) => (
          <button
            class="btn btn-sm btn-secondary"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              openEditSheet(staff);
            }}
          >
            <span class="material-symbols-outlined text-2xl">table_edit</span>
          </button>
        ),
      },
    ];

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="group"
          title={t("settings.staff.directory_title")}
          description={t("settings.staff.directory_desc")}
        >
          {{
            actions: () => (
              <button
                disabled={isPending.value}
                class="btn btn-primary btn-sm gap-2 shadow-lg"
                onClick={openCreateSheet}
              >
                <span class="material-symbols-outlined text-base">add</span>
                {t("settings.staff.create_label")}
              </button>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent noPadding>
            {isPending.value && (
              <div class="flex h-80 items-center justify-center">
                <Loading />
              </div>
            )}

            {!isPending.value && isError.value && (
              <ErrorRenderer
                error={error.value instanceof Error ? error.value : null}
                message={t("settings.staff.load_error")}
                onRetry={() => void refetch()}
              />
            )}

            {!isPending.value &&
              !isError.value &&
              (!staffList.value || staffList.value.length === 0) && (
                <div class="flex min-h-[320px] flex-col items-center justify-center px-8 py-16 text-center">
                  <div class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-200">
                    <span class="material-symbols-outlined text-3xl text-base-content/40">
                      group
                    </span>
                  </div>
                  <p class="mb-2 font-medium text-base-content">
                    {t("settings.staff.empty_title")}
                  </p>
                  <p class="text-sm text-base-content/60">
                    {t("settings.staff.empty_description")}
                  </p>
                </div>
              )}

            {!isPending.value &&
              !isError.value &&
              staffList.value &&
              staffList.value.length > 0 && (
                <ResponsiveTable
                  columns={columns}
                  rows={staffList.value}
                  rowKey={(staff: Staff) => staff.id}
                  onRowClick={openEditSheet}
                  emptyMessage={t("settings.staff.empty_title")}
                />
              )}
          </AppSubpageContent>
        </AppSubpage>
        {isSheetOpen.value ? (
          <RightSheet
            isOpen={isSheetOpen.value}
            title={
              mode.value === "create"
                ? t("settings.staff.add_new_member")
                : (selectedStaff.value?.name ?? t("settings.staff.edit_member"))
            }
            onClose={closeSheet}
            width="560px"
          >
            <StaffForm
              mode={mode.value}
              staff={selectedStaff.value}
              onSubmit={
                mode.value === "create"
                  ? handleCreate
                  : (formData) =>
                      handleUpdate(selectedStaff.value!.id, formData)
              }
              onDelete={
                mode.value === "edit" && selectedStaff.value
                  ? () => handleDelete(selectedStaff.value!.id)
                  : undefined
              }
              onCancel={closeSheet}
            />
          </RightSheet>
        ) : null}
      </div>
    );
  },
});
